import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { ProBadge } from "@/components/ui/pro-badge";
import { Loader2 } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { useSubscription } from "@/contexts/SubscriptionContext";
import { useUserPreferences } from "@/contexts/UserPreferencesContext";
import { auth } from "@/lib/firebase";
import { PRICING } from "@/lib/pricing";

export function SettingsPage() {
  const { user } = useAuth();
  const {
    plan,
    isPro,
    subscriptionStatus,
    currentPeriodEnd,
    grammarUsageCount,
    openUpgradePrompt,
  } = useSubscription();
  const { preferences, setGender, setWalkthroughCompleted } = useUserPreferences();
  const [portalLoading, setPortalLoading] = useState(false);

  const handleManageSubscription = async () => {
    setPortalLoading(true);
    try {
      const currentUser = auth.currentUser;
      if (!currentUser) return;
      const token = await currentUser.getIdToken();
      const response = await fetch("/api/stripe-portal", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      });
      const data = await response.json();
      if (data.url) {
        window.location.href = data.url;
      }
    } catch (error) {
      console.error("Failed to open portal:", error);
    } finally {
      setPortalLoading(false);
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-semibold">Settings</h1>
        <p className="text-muted-foreground">Manage your account and subscription</p>
      </div>

      {/* Account */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Account</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="flex items-center justify-between">
            <span className="text-sm text-muted-foreground">Name</span>
            <span className="text-sm font-medium">{user?.displayName || "—"}</span>
          </div>
          <div className="flex items-center justify-between">
            <span className="text-sm text-muted-foreground">Email</span>
            <span className="text-sm font-medium">{user?.email || "—"}</span>
          </div>
        </CardContent>
      </Card>

      {/* Grammar Gender */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">I am</CardTitle>
          <CardDescription>
            Default gender for Hebrew verb conjugations
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex gap-2">
            <Button
              variant={preferences.gender === "male" ? "default" : "outline"}
              onClick={() => setGender("male")}
              className="flex-1"
            >
              Male
            </Button>
            <Button
              variant={preferences.gender === "female" ? "default" : "outline"}
              onClick={() => setGender("female")}
              className="flex-1"
            >
              Female
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* App Tour */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">App Tour</CardTitle>
          <CardDescription>
            Re-watch the intro walkthrough
          </CardDescription>
        </CardHeader>
        <CardContent>
          <Button
            variant="outline"
            onClick={() => setWalkthroughCompleted(false)}
          >
            Replay Tutorial
          </Button>
        </CardContent>
      </Card>

      {/* Subscription */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2">
            Subscription
            {isPro && <ProBadge />}
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center justify-between">
            <span className="text-sm text-muted-foreground">Plan</span>
            <span className="text-sm font-medium capitalize">{plan}</span>
          </div>

          {isPro && (
            <>
              <div className="flex items-center justify-between">
                <span className="text-sm text-muted-foreground">Status</span>
                <Badge
                  variant={subscriptionStatus === "canceling" ? "secondary" : "default"}
                >
                  {subscriptionStatus === "canceling"
                    ? "Canceling"
                    : subscriptionStatus === "past_due"
                    ? "Past Due"
                    : "Active"}
                </Badge>
              </div>
              {currentPeriodEnd && (
                <div className="flex items-center justify-between">
                  <span className="text-sm text-muted-foreground">
                    {subscriptionStatus === "canceling"
                      ? "Access until"
                      : "Next billing date"}
                  </span>
                  <span className="text-sm font-medium">
                    {currentPeriodEnd.toLocaleDateString()}
                  </span>
                </div>
              )}
              {subscriptionStatus === "canceling" && (
                <p className="text-sm text-amber-600 bg-amber-50 p-3 rounded-md">
                  Your Pro plan is active until{" "}
                  {currentPeriodEnd?.toLocaleDateString()}. After that you'll be
                  on the Free plan.
                </p>
              )}
              <Button
                variant="outline"
                onClick={handleManageSubscription}
                disabled={portalLoading}
                className="w-full"
              >
                {portalLoading ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Loading...
                  </>
                ) : (
                  "Manage Subscription"
                )}
              </Button>
            </>
          )}

          {!isPro && (
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <span className="text-sm text-muted-foreground">
                  Grammar analyses this month
                </span>
                <span className="text-sm font-medium">
                  {grammarUsageCount} / 10 used
                </span>
              </div>
              <div className="space-y-2">
                <Button
                  onClick={() => openUpgradePrompt()}
                  className="w-full bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600 text-white"
                >
                  Upgrade to Pro —{" "}
                  {PRICING.isPromoActive && (
                    <span className="line-through opacity-70 mr-1">
                      ${PRICING.originalPrice}
                    </span>
                  )}
                  ${PRICING.currentPrice}/month
                </Button>
                {PRICING.isPromoActive && (
                  <p className="text-center text-xs font-medium text-amber-600">
                    {PRICING.promoName} — Limited time offer
                  </p>
                )}
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
